import { Fragment, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ChevronDown, ChevronRight } from "lucide-react";
import { api } from "../api";
import { PageHead, Filters, Panel, LoadingBlock } from "../shell";
import { money, pct, growthClass } from "../lib/format";

export function Branches({ dataset, period, setPeriod, year, setYear, startDate, setStartDate, endDate, setEndDate, periodParams }) {
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    setData(null); setOpenId(null); setDetail(null);
    api.get("/analytics/groups", { params: periodParams }).then((r) => setData(r.data));
  }, [period, year, startDate, endDate]);
  const toggle = async (id) => {
    if (openId === id) { setOpenId(null); setDetail(null); return; }
    setOpenId(id); setDetail(null);
    const r = await api.get(`/analytics/groups/${id}`, { params: periodParams });
    setDetail(r.data);
  };
  return (
    <main className="content">
      <PageHead eyebrow="PORTFÖY KIRILIMI" title="Branş Analizi" desc="Poliçe grubu ve branş bazında üretim." />
      <Filters period={period} setPeriod={setPeriod} year={year} setYear={setYear}
               startDate={startDate} setStartDate={setStartDate} endDate={endDate} setEndDate={setEndDate}
               isDemo={dataset?.state?.active === "DEMO"} />
      {!data ? <LoadingBlock/> : (
        <div className="panel table-panel">
          <table data-testid="groups-table">
            <thead>
              <tr>
                <th>Poliçe grubu</th>
                <th style={{textAlign:"right"}}>Net prim</th>
                <th style={{textAlign:"right"}}>Portföy payı</th>
                <th style={{textAlign:"right"}}>Komisyon</th>
                <th style={{textAlign:"right"}}>Komisyon payı</th>
                <th style={{textAlign:"right"}}>Poliçe</th>
                <th style={{textAlign:"right"}}>GY prim</th>
                <th>Büyüme</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((g) => (
                <Fragment key={g.id}>
                  <tr className="clickable" onClick={() => toggle(g.id)}>
                    <td>
                      <div className="perf-label">
                        {openId === g.id ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
                        <span className="tag">{g.code}</span> <strong>{g.name}</strong>
                      </div>
                    </td>
                    <td style={{textAlign:"right"}}>{money(g.premium, true)}</td>
                    <td style={{textAlign:"right"}}>{pct(g.portfolio_share, 2)}</td>
                    <td style={{textAlign:"right"}}>{money(g.commission, true)}</td>
                    <td style={{textAlign:"right"}}>{pct(g.commission_share, 2)}</td>
                    <td style={{textAlign:"right"}}>{new Intl.NumberFormat("tr-TR").format(g.count)}</td>
                    <td style={{textAlign:"right"}}>{money(g.previous, true)}</td>
                    <td className={growthClass(g.growth)}>{g.growth !== null ? pct(g.growth) : "—"}</td>
                  </tr>
                  {openId === g.id && (
                    <tr><td colSpan={8} style={{ background: "#f7f9fc", padding: 12 }}>
                      {!detail ? "Yükleniyor..." : (
                        <table data-testid={`group-branches-${g.id}`}>
                          <thead><tr><th>Branş</th><th style={{textAlign:"right"}}>Prim</th><th style={{textAlign:"right"}}>Komisyon</th><th style={{textAlign:"right"}}>Adet</th><th style={{textAlign:"right"}}>Pay</th><th>Büyüme</th></tr></thead>
                          <tbody>{detail.branches.map((b) => (
                            <tr key={b.name}><td><strong>{b.name}</strong></td>
                              <td style={{textAlign:"right"}}>{money(b.premium, true)}</td>
                              <td style={{textAlign:"right"}}>{money(b.commission, true)}</td>
                              <td style={{textAlign:"right"}}>{b.count}</td>
                              <td style={{textAlign:"right"}}>{pct(b.share, 2)}</td>
                              <td className={growthClass(b.growth)}>{b.growth !== null ? pct(b.growth) : "—"}</td></tr>
                          ))}</tbody>
                        </table>
                      )}
                    </td></tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}

export function Companies({ dataset, period, setPeriod, year, setYear, startDate, setStartDate, endDate, setEndDate, periodParams }) {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    setData(null);
    api.get("/analytics/companies", { params: periodParams }).then((r) => setData(r.data));
  }, [period, year, startDate, endDate]);
  return (
    <main className="content">
      <PageHead eyebrow="PORTFÖY KIRILIMI" title="Sigorta Şirketleri" desc="Şirket yoğunlaşması ve üretim trendleri." />
      <Filters period={period} setPeriod={setPeriod} year={year} setYear={setYear}
               startDate={startDate} setStartDate={setStartDate} endDate={endDate} setEndDate={setEndDate}
               isDemo={dataset?.state?.active === "DEMO"} />
      {!data ? <LoadingBlock/> : (
        <div className="panel table-panel">
          <table data-testid="companies-table">
            <thead>
              <tr>
                <th>Sigorta şirketi</th>
                <th style={{textAlign:"right"}}>Net üretim</th>
                <th style={{textAlign:"right"}}>Komisyon</th>
                <th style={{textAlign:"right"}}>Poliçe</th>
                <th style={{textAlign:"right"}}>Efektif %</th>
                <th style={{textAlign:"right"}}>Portföy payı</th>
                <th style={{textAlign:"right"}}>GY prim</th>
                <th>Büyüme</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((c) => (
                <tr key={c.id} className="clickable" onClick={() => navigate(`/companies/${c.id}`)}>
                  <td><strong>{c.name}</strong> <span className="muted" style={{ fontSize: 10 }}>{c.code}</span></td>
                  <td style={{textAlign:"right"}}>{money(c.premium, true)}</td>
                  <td style={{textAlign:"right"}}>{money(c.commission, true)}</td>
                  <td style={{textAlign:"right"}}>{new Intl.NumberFormat("tr-TR").format(c.count)}</td>
                  <td style={{textAlign:"right"}}>{pct(c.effective_rate, 2)}</td>
                  <td style={{textAlign:"right"}}>{pct(c.portfolio_share, 2)}</td>
                  <td style={{textAlign:"right"}}>{money(c.previous, true)}</td>
                  <td className={growthClass(c.growth)}>{c.growth !== null ? pct(c.growth) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}

export function CompanyDetail({ dataset, period, setPeriod, year, setYear, startDate, setStartDate, endDate, setEndDate, periodParams }) {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    setData(null);
    api.get(`/analytics/companies/${id}`, { params: periodParams }).then((r) => setData(r.data));
  }, [id, period, year, startDate, endDate]);
  if (!data) return <main className="content"><LoadingBlock/></main>;
  const k = data.kpis;
  return (
    <main className="content">
      <button className="back-link" onClick={() => navigate("/companies")}><ArrowLeft size={14}/> Tüm şirketler</button>
      <PageHead eyebrow="ŞİRKET DETAYI" title={data.company.name}
        desc={`Şirket kodu ${data.company.code || "—"} · ${k.count} hareket`}/>
      <Filters period={period} setPeriod={setPeriod} year={year} setYear={setYear}
               startDate={startDate} setStartDate={setStartDate} endDate={endDate} setEndDate={setEndDate}
               isDemo={dataset?.state?.active === "DEMO"} />
      <section className="kpi-grid">
        <div className="kpi"><div className="kpi-label">Net üretim</div><strong>{money(k.premium, true)}</strong></div>
        <div className="kpi"><div className="kpi-label">Komisyon</div><strong>{money(k.commission, true)}</strong></div>
        <div className="kpi"><div className="kpi-label">Poliçe</div><strong>{new Intl.NumberFormat("tr-TR").format(k.count)}</strong></div>
        <div className="kpi"><div className="kpi-label">Efektif %</div><strong>{pct(k.effective_rate, 2)}</strong></div>
      </section>
      <section className="lower-grid">
        <Panel title="Poliçe grubu dağılımı">
          <table><thead><tr><th>Grup</th><th style={{textAlign:"right"}}>Prim</th><th style={{textAlign:"right"}}>Komisyon</th></tr></thead>
            <tbody>{data.groups.map((g) => <tr key={g.name}><td><strong>{g.name}</strong></td>
              <td style={{textAlign:"right"}}>{money(g.premium, true)}</td>
              <td style={{textAlign:"right"}}>{money(g.commission, true)}</td></tr>)}</tbody></table>
        </Panel>
        <Panel title="Bölge dağılımı">
          <table><thead><tr><th>Bölge</th><th style={{textAlign:"right"}}>Prim</th><th style={{textAlign:"right"}}>Komisyon</th></tr></thead>
            <tbody>{data.regions.map((r) => <tr key={r.name}><td><strong>{r.name}</strong></td>
              <td style={{textAlign:"right"}}>{money(r.premium, true)}</td>
              <td style={{textAlign:"right"}}>{money(r.commission, true)}</td></tr>)}</tbody></table>
        </Panel>
      </section>
      <Panel title="Satıcı dağılımı" sub="En yüksek üretim yapan 20 satıcı">
        <table><thead><tr><th>Satıcı</th><th style={{textAlign:"right"}}>Prim</th><th style={{textAlign:"right"}}>Komisyon</th></tr></thead>
          <tbody>{data.sellers.map((s) => <tr key={s.name}><td><strong>{s.name}</strong></td>
            <td style={{textAlign:"right"}}>{money(s.premium, true)}</td>
            <td style={{textAlign:"right"}}>{money(s.commission, true)}</td></tr>)}</tbody></table>
      </Panel>
    </main>
  );
}
